import React, { useState, useEffect, useRef } from "react";
import TextField from "./TextField";
import Select from "react-select";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import "react-phone-number-input/style.css";

const PhoneInputTextField = ({ openTop, disabled, maxWidth, placeholder, value, onChange }) => {
    const [country, setCountry] = useState("FR");
    const [localValue, setLocalValue] = useState("");
    const [formatted, setFormatted] = useState("");
    const [menuOpenedWidth, setMenuOpenedWidth] = useState("100%");
    const [menuTarget, setMenuTarget] = useState(null);

    const selectRef = useRef();




    useEffect(() => {
        const detectUserCountry = async () => {
            try {
                const res = await fetch("https://ipapi.co/json/");
                const data = await res.json();
                if (data && data.country) {
                    setCountry(data.country);
                }
            }
            catch (error) {
                setCountry("FR");
            }
        };

        detectUserCountry();
    }, []);



    useEffect(() => {
        if (selectRef.current) {
            setMenuOpenedWidth(selectRef.current.offsetWidth + "px");
        }
    }, [selectRef]);



    const formatPhoneNumberWithNationalPrefix = (digits, countryCode) => {
        const callingCode = getCountryCallingCode(countryCode);
        const fullNumber = "+" + callingCode + digits;

        const parsed = parsePhoneNumberFromString(fullNumber);

        if (!parsed || parsed.country !== countryCode) {
            return digits;
        }

        let formatted = parsed.formatNational();

        // Add dot between number
        formatted = formatted.replace(/\s/g, ".");

        return formatted;
    };




    const excludedCountries = ["AC", "SH"]; // ← Flag's codes hidden on select option (Don't have svg's flag)



    const getCountryLabel = (code) =>
        new Intl.DisplayNames(["fr"], { type: "region" }).of(code);



    const countryOptions = getCountries()
        .filter(code => !excludedCountries.includes(code))
        .map((code) => ({
            value: code,
            label: getCountryLabel(code),
            callingCode: `+${getCountryCallingCode(code)}`,
            flag: `/country-flags/${code}.svg`
        }));



    // Custom display option selected (closed select)
    const SingleValue = ({ data }) =>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={data.flag} alt={data.value} style={{ width: 21, height: 14 }} />
            <span>{data.callingCode}</span>
        </div>;



    const Option = ({ data, innerRef, innerProps }) =>
        <div
            ref={innerRef}
            {...innerProps}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px"
            }}
        >
            <img src={data.flag} alt={data.label} style={{ width: 21, height: 14 }} />
            <span style={{ fontSize: 14 }}>{data.label}</span>
            <span style={{ fontSize: 14, color: "var(--grey-base)" }}>{data.callingCode}</span>
        </div>
    ;



    // Update from external value
    useEffect(() => {
        if (value) {
            const parsed = parsePhoneNumberFromString(value);
            if (parsed) {
                setCountry(parsed.country || "FR");
                const national = parsed.nationalNumber;
                const formatted = parsed.formatNational().replace(/\s/g, ".");
                setLocalValue(national);
                setFormatted(formatted);
            }
        }
        else {
            setLocalValue("");
            setFormatted("");
        }
    }, [value]);



    const handleChangeInput = (e) => {
        const digits = e.target.value.replace(/\D/g, "");
        setLocalValue(digits);

        const callingCode = getCountryCallingCode(country);
        const fullNumberDigits = "+" + callingCode + digits;
        const parsed = parsePhoneNumberFromString(fullNumberDigits);

        const national = parsed ? parsed.nationalNumber : null;
        const fullNumber = "+" + callingCode + national;
        const formatted = formatPhoneNumberWithNationalPrefix(digits, country);
        setFormatted(formatted);

        if (onChange) {
            onChange(fullNumber);
        }
    };



    const handleChangeCountry = (selectedCode) => {
        setCountry(selectedCode);
        setLocalValue("");
        setFormatted("");
        if (onChange) {onChange("");}
    };




    // When select is opened, options will be on top of modal and not hidden from overflow
    useEffect(() => {
        const modalElement = document.querySelector(".modal");
        if (modalElement) {setMenuTarget(modalElement);}
    }, []);




    return (
        <div className="container-phone-input" ref={selectRef} style={{
            display: "flex",
            alignItems: "center",
            flexGrow: 1
        }}>
            {/* Select country */}
            <div style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#EBEBEB",
                borderRadius: "6px 0 0 0px",
                boxShadow: " 0px -1px 0px 0px var(--grey-base) inset",
                height: "48px",
                position: "relative"
            }}>
                <Select
                    menuPlacement={openTop ? "top" : "bottom"}
                    menuPortalTarget={menuTarget}
                    menuPosition="fixed"
                    value={countryOptions.find(opt => opt.value === value)}
                    onChange={(opt) => handleChangeCountry(opt.value)}
                    options={countryOptions}
                    isSearchable={false}
                    components={{ SingleValue, Option }}
                    isDisabled={disabled}
                    placeholder={<div style={{ display: "flex", gap: "10px", alignItems: "center", pointerEvents: "none" }}>
                        <img src={`/country-flags/${country}.svg`} alt="flag country" style={{ width: "21px", height: "14px" }} />
                        <span style={{ fontSize: "14px" }}>+{getCountryCallingCode(country)}</span>
                    </div>}
                    styles={{
                        control: (base) => ({
                            ...base,
                            height: 46,
                            minWidth: 79,
                            backgroundColor: "#EBEBEB",
                            border: "none",
                            borderRadius: "6px 0 0 6px",
                            boxShadow: "none",
                            display: "flex",
                            alignItems: "center"
                        }),
                        placeholder: (base) => ({ ...base, color: "var(--carbone)" }),
                        dropdownIndicator: (base) => ({ ...base, display: "none" }),
                        indicatorSeparator: () => ({ display: "none" }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            width: menuOpenedWidth // Width menu opened
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 8px"
                        }),
                        singleValue: (base) => ({
                            ...base,
                            display: "flex",
                            alignItems: "center",
                            padding: 0,
                            margin: 0
                        }),
                        input: (base) => ({
                            // Hidden input for search name country
                            ...base,
                            margin: 0,
                            padding: 0,
                            height: 0,
                            maxHeight: 0,
                            overflow: "hidden"
                        })
                    }}
                />
            </div>

            <TextField
                disabled={disabled}
                maxWidth={maxWidth}
                placeholder={placeholder}
                value={formatted}
                onChange={handleChangeInput}
            />
        </div>
    );
};

export default PhoneInputTextField;
